<template>
  <div>
    <Panel :icon="['fas', 'money-check-alt']">
      <template #header>
        <span data-cy="heading">Account Summary</span>
      </template>

      <template #content>
        <div class="w-100">
          <div class="billing-widgets-grid">
            <!-- Available Balance Widget -->
            <div class="billing-widget widget-balance">
              <div class="widget-icon">
                <font-awesome-icon :icon="['fas', 'wallet']" />
              </div>
              <div class="widget-content">
                <span class="widget-label">Available Balance</span>
                <span 
                  class="widget-value" 
                  :class="{'text-danger': isLowBalance}"
                >
                  {{ formatCurrency(availableBalance) }}
                </span>
                <small v-if="availableBalance <= 0 && isPostPay" class="widget-subtext text-danger mt-1" style="font-weight: 600;">
                  Top-up required to buy calls
                </small>
              </div>
              <div class="widget-decoration">
                <font-awesome-icon :icon="['fas', 'wallet']" />
              </div>
            </div>

            <!-- Calls Purchased Widget -->
            <div 
              v-if="isPostPay" 
              class="billing-widget widget-calls"
              v-b-tooltip.hover
              title="Estimated usage charges for the current billing cycle. These have not yet been invoiced."
            >
              <div class="widget-icon">
                <font-awesome-icon :icon="['fas', 'phone-volume']" />
              </div>
              <div class="widget-content">
                <span class="widget-label">Unbilled Usage</span>
                <div class="d-flex flex-column">
                  <span class="widget-value">{{ formatCurrency(purchasedCallsAmount) }}</span>
                  <small class="widget-subtext">{{ purchasedCallsCount }} calls since last cycle</small>
                </div>
              </div>
              <div class="widget-decoration">
                <font-awesome-icon :icon="['fas', 'chart-line']" />
              </div>
            </div>

            <!-- Unpaid Invoices Widget -->
            <div v-if="isPostPay" class="billing-widget widget-unpaid">
              <div class="widget-icon">
                <font-awesome-icon :icon="['fas', 'file-invoice-dollar']" />
              </div>
              <div class="widget-content">
                <span class="widget-label">Payment Required</span>
                <div class="d-flex flex-column">
                  <span class="widget-value">{{ formatCurrency(unpaidInvoices.total) }}</span>
                  <small class="widget-subtext">{{ unpaidInvoices.count }} invoices pending</small>
                </div>
              </div>
              <div class="widget-decoration">
                <font-awesome-icon :icon="['fas', 'file-invoice-dollar']" />
              </div>
            </div>

            <!-- Pending Payments (Seller only) -->
            <div 
              v-if="!isBuyerAccount" 
              class="billing-widget widget-pending"
            >
              <div class="widget-icon">
                <font-awesome-icon :icon="['fas', 'hand-holding-usd']" />
              </div>
              <div class="widget-content">
                <span class="widget-label">Pending Payments</span>
                <span class="widget-value">{{ formatCurrency(sellerPendingBalance) }}</span>
              </div>
              <div class="widget-decoration">
                <font-awesome-icon :icon="['fas', 'hand-holding-usd']" />
              </div>
            </div>
          </div>
        </div>
      </template>
    </Panel>

    <div 
      v-if="showTabs"
      class="mt-4" 
    >
      <b-tabs 
        v-model="tabIndex"
        content-class="mt-3"
      >
        <b-tab
          v-if="showCreditCardCharges"
          title="Payment Receipts"
        >
          <CreditCardCharges />
        </b-tab>
        <b-tab
          v-if="showInvoices"
          title="Payment History"
        >
          <Invoices />
        </b-tab>
        <b-tab
          title="Balance History"
        >
          <BalanceHistory />
        </b-tab>
      </b-tabs>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'
import formatCurrency from '@/utils/formatCurrency'
import CreditCardCharges from '../buyer/CreditCardCharges.vue'
import Invoices from '../billing/Invoices.vue'
import BalanceHistory from './BalanceHistory.vue'

export default {
  name: 'MyAccount',

  title: 'My Account',

  components: {
    CreditCardCharges,
    Invoices,
    BalanceHistory
  },

  data() {
    return {
      loading: false,
      details: {},
      tabIndex: 0
    }
  },
  computed: {
    ...mapState('billing', ['accountSummary', 'accountPayments', 'purchasedCalls']),
    ...mapState('dashboard', ['chargeReceipts', 'creditCardChargesFetched']),
    ...mapGetters('auth', ['isBuyerAccount', 'accountDetails']),
    ...mapGetters('billing', ['sellerPendingBalance', 'accountBillingConfiguration']),
    availableBalance() {
      if (!this.accountSummary) return 0
      
      let balance = this.accountSummary.cashBalance
      // If post-pay, calculating remaining credit: Current Balance - Minimum Balance (which is negative)
      if (this.isPostPay) {
        const minBalance = this.accountBillingConfiguration?.minimumBalance || 0
        balance = balance - minBalance
      }
      
      // Ensure we never show negative available balance
      return Math.max(0, balance)
    },
    isPostPay() {
      return this.isBuyerAccount && this.accountDetails?.billingConfiguration?.prePay === false
    },
    isLowBalance() {
      if (!this.isPostPay) return false
      
      const limit = Math.abs(this.accountBillingConfiguration?.minimumBalance || 0)
      if (limit === 0) return false

      // Highlight if available balance is less than 5% of the total credit limit
      return this.availableBalance < (limit * 0.05)
    },
    purchasedCallsAmount() {
      return this.purchasedCalls?.purchasedCallsAmount || 0
    },
    purchasedCallsCount() {
      return this.purchasedCalls?.purchasedCallsCount || 0
    },
    unpaidInvoices() {
      if (!this.accountPayments || !this.accountPayments.length) {
        return { count: 0, total: 0 }
      }
      // Filter for statuses considered 'Unpaid'
      const unpaidStatuses = ['Outstanding', 'Overdue', 'Failed']
      
      const unpaid = this.accountPayments.filter(inv => unpaidStatuses.includes(inv.paymentStatusId))
      
      const total = unpaid.reduce((sum, inv) => sum + (inv.amountDue || 0), 0)
      
      return {
        count: unpaid.length,
        total: total
      }
    },
    showCreditCardCharges() {
      if (!this.isBuyerAccount) {
        return false
      }
      const isPrePay = this.accountDetails?.billingConfiguration?.prePay
      // Pre-pay buyers always see credit card charges
      if (isPrePay) {
        return true
      }
      // Post-paid buyers only see credit card charges if they have any
      // Wait for the fetch to complete before deciding to avoid race condition
      return this.creditCardChargesFetched && this.chargeReceipts && this.chargeReceipts.length > 0
    },
    showInvoices() {
      return !this.isBuyerAccount || !this.accountDetails?.billingConfiguration?.prePay
    },
    showTabs() {
      // Always show tabs now that we have common tabs like Balance History
      return true
    },
  },
  watch: {
    showCreditCardCharges(val) {
      if (val) {
        this.$nextTick(() => {
          this.tabIndex = 0
        })
      }
    }
  },
  async created() {
    await this.getAccountBillingSummary()
    if (this.isBuyerAccount) {
      // Load payments to populate Unpaid Invoices widget
      // We don't await this to avoid blocking the UI render
      await this.getAccountPayments()
      this.fetchPurchasedCalls()
      await this.getCreditCardCharges()
    }
  },
  methods: {
    ...mapActions('billing', ['getAccountBillingSummary', 'getAccountPayments', 'fetchPurchasedCalls']),
    ...mapActions('dashboard', ['getCreditCardCharges']),
    formatCurrency,

  }
}
</script>

<style lang="scss" scoped>
::v-deep .nav-tabs {
  border-bottom: 1px solid #4a5a6a;
  margin-left: 1.5rem;
  margin-right: 1.5rem;

  .nav-link {
    /* Inactive State: Darker, muted, visually 'behind' */
    color: #8898aa;
    background-color: rgba(0, 0, 0, 0.2); 
    border: 1px solid transparent;
    border-bottom: none;
    margin-right: 4px;
    margin-bottom: -1px;
    font-weight: 500;
    padding: 0.8rem 1.5rem;
    border-radius: 4px 4px 0 0;
    transition: all 0.2s ease;

    &:hover {
      /* Hover State: Slightly lighter, hinting interaction */
      color: #d0d0d0;
      background-color: rgba(255, 255, 255, 0.05);
      border-color: #4a5a6a;
      border-bottom: none;
    }

    &.active {
      /* Active State: Bright, connected to content, distinct highlight */
      color: #ffffff;
      background-color: var(--component-panel-bg, #1e2634);
      border: 1px solid #4a5a6a;
      border-bottom-color: var(--component-panel-bg, #1e2634); 
      box-shadow: inset 0 3px 0 0 #00ceff; /* Strong top accent */
      font-weight: 600; /* Make text bolder */
    }
  }
}

::v-deep .tab-content {
  margin-top: 0 !important;
  
  .panel {
    border: 1px solid #4a5a6a; 
    border-top: none;
    margin-top: 0;
    border-top-left-radius: 0;
    border-top-right-radius: 0;
    /* Ensure panel background matches active tab to create the 'merged' effect */
    background-color: var(--component-panel-bg, #1e2634); 
  }
}

.billing-widgets-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 1.5rem;
  padding: 1rem 0 2rem 0;
}

.billing-widget {
  background: var(--component-panel-bg, #1e2634);
  border-radius: 12px;
  padding: 1.5rem;
  display: flex;
  align-items: center;
  position: relative;
  overflow: hidden;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  border: 1px solid rgba(255, 255, 255, 0.05);
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 15px rgba(0, 0, 0, 0.2);
  }

  .widget-icon {
    font-size: 1.8rem;
    width: 50px;
    height: 50px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-right: 1rem;
    flex-shrink: 0;
  }

  .widget-content {
    display: flex;
    flex-direction: column;
    z-index: 2;
  }

  .widget-label {
    font-size: 0.85rem;
    color: #8898aa;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: 0.25rem;
  }

  .widget-value {
    font-size: 1.5rem;
    font-weight: 700;
    color: #ffffff;
    line-height: 1.2;
  }

  .widget-subtext {
    font-size: 0.8rem;
    color: #8898aa;
    margin-top: 2px;
  }

  /* Decorative background element */
  &::before {
    content: '';
    position: absolute;
    top: -50%;
    right: -20%;
    width: 150px;
    height: 150px;
    border-radius: 50%;
    filter: blur(40px);
    opacity: 0.15;
    z-index: 1;
  }
}

/* Specific Widget Styles */
/* Shared Decoration Style */
.widget-decoration {
  position: absolute;
  right: 15px;
  bottom: 15px;
  font-size: 4rem;
  opacity: 0.08;
  transform: rotate(-15deg);
  pointer-events: none;
  z-index: 1;
}

/* Specific Widget Styles */
.widget-balance {
  .widget-icon {
    background: rgba(0, 255, 97, 0.1);
    color: #00ff61;
  }
  &::before { background: #00ff61; }
  border-left: 4px solid #00ff61;
  
  .widget-decoration {
    color: #00ff61;
  }
}

.widget-unpaid {
  .widget-icon {
    background: rgba(255, 71, 87, 0.1);
    color: #ff4757;
  }
  &::before { background: #ff4757; }
  border-left: 4px solid #ff4757;

  .widget-decoration {
    color: #ff4757;
  }
}

.widget-calls {
  .widget-icon {
    background: rgba(46, 134, 222, 0.1);
    color: #2e86de;
  }
  &::before { background: #2e86de; }
  border-left: 4px solid #2e86de;
  
  .widget-decoration {
    color: #2e86de;
  }
}

.widget-pending {
  .widget-icon {
    background: rgba(0, 206, 255, 0.1);
    color: #00ceff;
  }
  &::before { background: #00ceff; }
  border-left: 4px solid #00ceff;

  .widget-decoration {
    color: #00ceff;
  }
}
</style>
